import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PermissionProvider } from '@/contexts/PermissionContext';

// Create a client instance outside the component to prevent recreation on every render
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Layout Components
import Layout from '@/components/layout/Layout';

// Page Components
import SimpleAuthPage from '@/pages/auth/SimpleAuthPage';
import DashboardPage from '@/pages/DashboardPage';
import NotFoundPage from '@/pages/NotFoundPage';
import POSPage from '@/pages/POSPage';
import ProductsPage from '@/pages/ProductsPage';
import UsersPage from '@/pages/UsersPage';
import ConfigPage from '@/pages/ConfigPage';
import SalesHistoryPage from '@/pages/SalesHistoryPage';
import ReportsPage from '@/pages/ReportsPage';
import AuditPage from '@/pages/AuditPage';
import InformalEconomyPage from '@/pages/InformalEconomyPage';
import OfflineEnginePage from '@/pages/OfflineEnginePage';
import PhasedCompliancePage from '@/pages/PhasedCompliancePage';
import SmartCorrectionsPage from '@/pages/SmartCorrectionsPage';
import BusinessIntelligencePage from '@/pages/BusinessIntelligencePage';
import IntegrationPage from '@/pages/IntegrationPage';
import SyncIntelligencePage from '@/pages/SyncIntelligencePage';
import SettingsPage from '@/pages/SettingsPage';
import ContactsPage from '@/pages/ContactsPage';
import ExpensesPage from '@/pages/ExpensesPage';
import PurchasesPage from '@/pages/PurchasesPage';
import StockAdjustmentsPage from '@/pages/StockAdjustmentsPage';
import BranchesPage from '@/pages/BranchesPage';
import StockTransfersPage from '@/pages/StockTransfersPage';
import InventoryUploadPage from '@/pages/InventoryUploadPage';
import AccountingPage from '@/pages/AccountingPage';
import BusinessProfilePage from '@/pages/BusinessProfilePage';
import DocumentVaultPage from '@/pages/DocumentVaultPage';
import ExpenseProofPage from '@/pages/ExpenseProofPage';
import SmartLedgerPage from '@/pages/SmartLedgerPage';
import DocumentsPage from '@/pages/DocumentsPage';
import TestPage from '@/pages/TestPage';

// Loading Component
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import OfflineStatusIndicator from '@/components/OfflineStatusIndicator';

function App() {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const RequireAuth = () => {
    if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
    return <Outlet />;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <PermissionProvider>
      <>
        <Routes>
        <Route
          path="/auth/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SimpleAuthPage />}
        />

        <Route element={<RequireAuth />}>
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="pos" element={<POSPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="config" element={<ConfigPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="sales" element={<SalesHistoryPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="purchases" element={<PurchasesPage />} />
            <Route path="stock-adjustments" element={<StockAdjustmentsPage />} />
            <Route path="branches" element={<BranchesPage />} />
            <Route path="stock-transfers" element={<StockTransfersPage />} />
            <Route path="inventory-upload" element={<InventoryUploadPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="accounting" element={<AccountingPage />} />
            <Route path="business-profile" element={<BusinessProfilePage />} />
            <Route path="document-vault" element={<DocumentVaultPage />} />
            <Route path="expense-proof" element={<ExpenseProofPage />} />
            <Route path="smart-ledger" element={<SmartLedgerPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="test" element={<TestPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="informal-economy" element={<InformalEconomyPage />} />
            <Route path="offline-engine" element={<OfflineEnginePage />} />
            <Route path="phased-compliance" element={<PhasedCompliancePage />} />
            <Route path="/smart-corrections" element={<SmartCorrectionsPage />} />
            <Route path="/business-intelligence" element={<BusinessIntelligencePage />} />
            <Route path="/integration" element={<IntegrationPage />} />
            <Route path="/sync-intelligence" element={<SyncIntelligencePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      
      {/* Offline Status Indicator - shown on all authenticated pages */}
      {isAuthenticated && <OfflineStatusIndicator />}
      </>
    </PermissionProvider>
    </QueryClientProvider>
  );
}

export default App;
